import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

import { meterReadingsApi, billingApi } from '../../services/api';

// Formattazione numeri
const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined || isNaN(num)) return '-';
  return parseFloat(num).toLocaleString('it-IT', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
};

// Formattazione valuta
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return '-';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

// Formattazione data
const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('it-IT');
};

// Colori stato
const getStatoColor = (stato) => {
  switch (stato) {
    case 'inserita': return 'default';
    case 'verificata': return 'info';
    case 'fatturata': return 'success';
    case 'annullata': return 'error';
    default: return 'default';
  }
};

const ReadingListTab = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentYear = new Date().getFullYear();
  const queryClient = useQueryClient();
  
  const [anno, setAnno] = useState(currentYear);
  const [bimestre, setBimestre] = useState('');
  const [stato, setStato] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Stato ordinamento
  const [orderBy, setOrderBy] = useState('progressivo');
  const [order, setOrder] = useState('asc');
  
  // Stato per dialog conferma eliminazione
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [readingToDelete, setReadingToDelete] = useState(null);
  
  // Mutation per eliminare lettura
  const deleteMutation = useMutation({
    mutationFn: (id) => meterReadingsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['meter-readings']);
      setDeleteDialogOpen(false);
      setReadingToDelete(null);
    },
    onError: (error) => {
      console.error('Errore eliminazione:', error);
      alert(error.response?.data?.message || 'Errore durante l\'eliminazione');
    }
  });

  // Mutation per validare lettura (inserita → verificata)
  const validateMutation = useMutation({
    mutationFn: (id) => meterReadingsApi.update(id, { stato: 'verificata' }),
    onSuccess: () => {
      queryClient.invalidateQueries(['meter-readings']);
    },
    onError: (error) => {
      console.error('Errore validazione:', error);
      alert(error.response?.data?.message || 'Errore durante la validazione');
    }
  });
  
  // Handler per aprire dialog conferma
  const handleDeleteClick = (reading) => {
    setReadingToDelete(reading);
    setDeleteDialogOpen(true);
  };
  
  // Handler per confermare eliminazione
  const handleConfirmDelete = () => {
    if (readingToDelete) {
      deleteMutation.mutate(readingToDelete.id);
    }
  };

  // Handler per ordinamento
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Query periodi
  const periodsQuery = useQuery({
    queryKey: ['billing-periods'],
    queryFn: () => billingApi.getPeriods().then(res => res.data?.data || [])
  });

  // Query letture
  const readingsQuery = useQuery({
    queryKey: ['meter-readings', anno, bimestre, stato, page, rowsPerPage, orderBy, order],
    queryFn: () => meterReadingsApi.getAll({
      anno: anno || undefined,
      bimestre: bimestre || undefined,
      stato: stato || undefined,
      page: page + 1,
      limit: rowsPerPage,
      sortBy: orderBy,
      sortOrder: order
    }).then(res => res.data)
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const bimestreLabels = {
    1: 'GEN-FEB',
    2: 'MAR-APR',
    3: 'MAG-GIU',
    4: 'LUG-AGO',
    5: 'SET-OTT',
    6: 'NOV-DIC'
  };

  return (
    <Box>
      {/* Filtri */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Anno</InputLabel>
              <Select
                value={anno}
                label="Anno"
                onChange={(e) => { setAnno(e.target.value); setPage(0); }}
              >
                <MenuItem value="">Tutti</MenuItem>
                {[currentYear, currentYear - 1, currentYear - 2].map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Bimestre</InputLabel>
              <Select
                value={bimestre}
                label="Bimestre"
                onChange={(e) => { setBimestre(e.target.value); setPage(0); }}
              >
                <MenuItem value="">Tutti</MenuItem>
                {[1, 2, 3, 4, 5, 6].map(b => (
                  <MenuItem key={b} value={b}>{bimestreLabels[b]}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Stato</InputLabel>
              <Select
                value={stato}
                label="Stato"
                onChange={(e) => { setStato(e.target.value); setPage(0); }}
              >
                <MenuItem value="">Tutti</MenuItem>
                <MenuItem value="inserita">Inserita</MenuItem>
                <MenuItem value="verificata">Verificata</MenuItem>
                <MenuItem value="fatturata">Fatturata</MenuItem>
                <MenuItem value="annullata">Annullata</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Tooltip title="Aggiorna">
              <IconButton onClick={() => readingsQuery.refetch()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading */}
      {readingsQuery.isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Errore */}
      {readingsQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Errore nel caricamento delle letture: {readingsQuery.error?.message}
        </Alert>
      )}

      {/* Tabella */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ width: 80 }}>
                  <TableSortLabel
                    active={orderBy === 'progressivo'}
                    direction={orderBy === 'progressivo' ? order : 'asc'}
                    onClick={() => handleRequestSort('progressivo')}
                  >
                    Progr.
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'meter_number'}
                    direction={orderBy === 'meter_number' ? order : 'asc'}
                    onClick={() => handleRequestSort('meter_number')}
                  >
                    Contatore
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'cliente'}
                    direction={orderBy === 'cliente' ? order : 'asc'}
                    onClick={() => handleRequestSort('cliente')}
                  >
                    Cliente
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'periodo_riferimento'}
                    direction={orderBy === 'periodo_riferimento' ? order : 'asc'}
                    onClick={() => handleRequestSort('periodo_riferimento')}
                  >
                    Periodo
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'data_lettura'}
                    direction={orderBy === 'data_lettura' ? order : 'asc'}
                    onClick={() => handleRequestSort('data_lettura')}
                  >
                    Data Lettura
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'lettura_mc'}
                    direction={orderBy === 'lettura_mc' ? order : 'asc'}
                    onClick={() => handleRequestSort('lettura_mc')}
                  >
                    Lettura MC
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'consumo_mc'}
                    direction={orderBy === 'consumo_mc' ? order : 'asc'}
                    onClick={() => handleRequestSort('consumo_mc')}
                  >
                    Consumo MC
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'consumo_litri'}
                    direction={orderBy === 'consumo_litri' ? order : 'asc'}
                    onClick={() => handleRequestSort('consumo_litri')}
                  >
                    Consumo L
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'importo_totale'}
                    direction={orderBy === 'importo_totale' ? order : 'asc'}
                    onClick={() => handleRequestSort('importo_totale')}
                  >
                    Importo
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'stato'}
                    direction={orderBy === 'stato' ? order : 'asc'}
                    onClick={() => handleRequestSort('stato')}
                  >
                    Stato
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  Origine
                </TableCell>
                <TableCell align="center" sx={{ width: 100 }}>
                  Azioni
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {readingsQuery.data?.data?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      Nessuna lettura trovata
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {readingsQuery.data?.data?.map(reading => (
                <TableRow key={reading.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {reading.meter?.sotto_zona || reading.meter?.c_sotto_zo || reading.meter?.c_nu_conta || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {reading.meter?.meter_number || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {reading.meter?.customer 
                        ? `${reading.meter.customer.cognome || ''} ${reading.meter.customer.nome || ''}`.trim()
                        : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {reading.meter?.customer?.cod_cli}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {reading.periodo_riferimento}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {formatDate(reading.data_lettura)}
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(reading.lettura_mc, 2)}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      {reading.is_anomaly && (
                        <Tooltip title="Consumo anomalo">
                          <WarningIcon color="warning" fontSize="small" />
                        </Tooltip>
                      )}
                      {formatNumber(reading.consumo_mc, 2)}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(reading.consumo_litri, 0)}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(reading.importo_totale)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={reading.stato} 
                      size="small" 
                      color={getStatoColor(reading.stato)}
                    />
                  </TableCell>
                  <TableCell>
                    {reading.inserted_from === 'pwa' && (
                      <Chip
                        icon={reading.tipo_lettura === 'foto' ? <CameraAltIcon /> : <PhoneAndroidIcon />}
                        label={reading.tipo_lettura === 'foto' ? 'Foto' : 'PWA'}
                        size="small"
                        variant="outlined"
                        color={reading.tipo_lettura === 'foto' ? 'warning' : 'secondary'}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {reading.stato === 'inserita' && (
                      <Tooltip title="Valida lettura">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => validateMutation.mutate(reading.id)}
                          disabled={validateMutation.isPending}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {reading.stato !== 'fatturata' && reading.stato !== 'annullata' && (
                      <Tooltip title="Elimina lettura">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(reading)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={readingsQuery.data?.pagination?.total || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Righe per pagina"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
        />
      </Paper>
      
      {/* Dialog conferma eliminazione */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullScreen={isMobile}
      >
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare la lettura del contatore{' '}
            <strong>{readingToDelete?.meter?.meter_number}</strong> del{' '}
            {readingToDelete?.data_lettura ? new Date(readingToDelete.data_lettura).toLocaleDateString('it-IT') : ''}?
            <br /><br />
            La lettura verrà marcata come "annullata" e non sarà più visibile nelle fatturazioni.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Annulla
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReadingListTab;
